import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@kontentino/ui';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import PageTypeUtils from 'app/utils/pageType';

type Props = {
  // The pages Add would send, already-connected ones included.
  selectedCount: number;
  // The portfolios they would go out under.
  portfoliosCount: number;
  // Of those pages, the ones not connected yet: only these use plan slots.
  newSelectedCount: number;
  remainingProfiles: number | undefined;
  isOverLimit: boolean;
  isSubmitting: boolean;
  // A Reconnect/Refresh entry: the button says Reconnect, not Add.
  isReconnect: boolean;
  onSubmit: () => void;
  submitDataCy?: string;
  // Add's analytics hook: the one-list picker's confirm hook by default (the
  // modal), another host's own, or none (null).
  submitDataName?: string | null;
};

const MODAL_CONFIRM_DATA_NAME = `posts_add-profile-modal_${PageTypeUtils.getName(
  TYPE.FACEBOOK,
)}_confirm`;

// The picker's fixed footer: what Add would send, the plan limit when the new
// pages do not fit, and Add itself.
const PickerFooter: FC<Props> = ({
  selectedCount,
  portfoliosCount,
  newSelectedCount,
  remainingProfiles,
  isOverLimit,
  isSubmitting,
  isReconnect,
  onSubmit,
  submitDataCy = 'two-column-picker_add-button',
  submitDataName = MODAL_CONFIRM_DATA_NAME,
}) => {
  const { t } = useTranslation();

  return (
    <div
      data-cy="two-column-picker_footer"
      className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2 tw-border-t tw-border-grayscale-10 tw-pt-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between sm:tw-gap-3 sm:tw-pt-4"
    >
      <div className="tw-flex tw-flex-col tw-gap-1">
        <p className="tw-m-0 tw-text-md tw-text-grayscale-140">
          <strong className="tw-font-semibold tw-text-grayscale-180">
            {t('newSocialMediaProfile.twoColumn.pagesCount', {
              count: selectedCount,
            })}
          </strong>{' '}
          {t('newSocialMediaProfile.twoColumn.selectedAcross', {
            count: portfoliosCount,
          })}
        </p>
        {isOverLimit && (
          <p
            className="tw-m-0 tw-text-sm tw-font-medium tw-text-danger-100"
            data-cy="two-column-picker_limit-line"
          >
            {t('newSocialMediaProfile.twoColumn.limitLine', {
              newCount: newSelectedCount,
              // A plan already over its limit allows none, not fewer.
              remaining: Math.max(remainingProfiles ?? 0, 0),
            })}
          </p>
        )}
      </div>
      <Button
        size="large"
        disabled={selectedCount === 0}
        isLoading={isSubmitting}
        onClick={onSubmit}
        iconAfter={<Icon icon={faArrowRight} />}
        className="tw-w-full sm:tw-w-auto"
        data-cy={submitDataCy}
        data-name={submitDataName ?? undefined}
      >
        {t(
          isReconnect
            ? 'newSocialMediaProfile.twoColumn.reconnectPages'
            : 'newSocialMediaProfile.twoColumn.addPages',
          { count: selectedCount },
        )}
      </Button>
    </div>
  );
};

export default PickerFooter;
